import { useMemo } from 'react';
import { Helmet } from 'react-helmet';

export interface SEOHeaderProps {
  title?: string;
  description?: string;
  keywords?: string;
  url?: string;

  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;

  productPrice?: string | number;
  productCurrency?: string;
  productAvailability?: string;
  productCondition?: string;
  productName?: string;
  productCategory?: string;
  productImage?: string;

  storeName?: string;
  storeCategories?: string;
  storeContacts?: string;
  storeLocations?: string;
  storeLogo?: string;

  canonical?: string;
  robots?: string;

  siteName?: string;
  locale?: string;

  customMeta?: Array<Record<string, any>>;
}

const SEOHeader = ({
  title = 'Bazaarcheap - Create Your Free Business Profile',
  description = 'Start your free business profile on Bazaarcheap. Showcase your products or services, manage requests, and grow your client base.',
  keywords,
  url = 'https://www.bazaarcheap.com',

  ogTitle = title,
  ogDescription = description,
  ogImage = 'https://www.bazaarcheap.com/assets/og-image.png',
  ogUrl = url,
  ogType = 'website',

  productPrice,
  productCurrency = 'UGX',
  productAvailability = 'in stock',
  productCondition = 'new',
  productName,
  productCategory,
  productImage,

  storeName,
  storeCategories,
  storeContacts,
  storeLocations,
  storeLogo,

  canonical = url,
  robots = 'index, follow',

  siteName,
  locale = 'en_US',

  // Custom meta tags
  customMeta = [],
}: SEOHeaderProps) => {
  const storeJsonLd = useMemo(() => {
    if (!storeName || !storeLogo) return null;
    const json = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: storeName,
      image: storeLogo || ogImage,
      description: description,
      ...(storeCategories && { category: storeCategories }),
      ...(storeLocations && { locations: storeLocations }),
      ...(storeContacts && { contacts: storeContacts }),
    };
    return json;
  }, []);

  const productJsonLd = useMemo(() => {
    if (!productPrice && !productName) return null;

    return {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: title,
      description: description,
      ...(productName && { brand: { '@type': 'Name', name: productName } }),
      ...(productCategory && { category: productCategory }),
      ...{ image: productImage || ogImage },
      ...(productPrice && {
        offers: {
          '@type': 'Offer',
          price: productPrice,
          priceCurrency: productCurrency,
          availability: `https://schema.org/${productAvailability.replace(
            /\s+/g,
            ''
          )}`,
        },
      }),
    };
  }, [
    description,
    ogImage,
    productAvailability,
    productCategory,
    productCurrency,
    productImage,
    productName,
    productPrice,
    title,
  ]);
  console.log('Product JSON-LD:', productPrice, typeof productPrice,);

  const finalJsonLd = storeJsonLd || productJsonLd;
  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}

      <meta name="robots" content={robots} />

      {canonical && <link rel="canonical" href={canonical} />}

      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {(ogDescription || description) && (
        <meta
          property="og:description"
          content={ogDescription || description}
        />
      )}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      <meta property="og:type" content={ogType} />
      {siteName && <meta property="og:site_name" content={siteName} />}
      <meta property="og:locale" content={locale} />

      {productPrice && (
        <meta
          property="product:price:amount"
          content={productPrice!.toString()}
        />
      )}
      {productPrice && (
        <meta property="product:price:currency" content={productCurrency} />
      )}
      {productAvailability && (
        <meta property="product:availability" content={productAvailability} />
      )}
      {productCondition && (
        <meta property="product:condition" content={productCondition} />
      )}

      {customMeta.map((meta: any, index: any) => (
        <meta key={index} {...meta} />
      ))}

      {finalJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(finalJsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHeader;
